import clsx from 'clsx'
import type {
	DetailedHTMLProps,
	FunctionComponent,
	InputHTMLAttributes,
	LabelHTMLAttributes,
	ReactNode,
} from 'react'
import { Icon, IconName } from './Icon'
import styles from './Input.module.sass'
import { Tooltip } from './Tooltip'

export type InputProps = Pick<
	DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	| 'id'
	| 'name'
	| 'type'
	| 'autoComplete'
	| 'value'
	| 'onChange'
	| 'disabled'
	| 'readOnly'
	| 'placeholder'
	| 'required'
> & {
	label?: LabelProps
	iconDecoration?: IconName
	reactHookForm?: object
	explanation?: string
}

type LabelProps = Pick<
	DetailedHTMLProps<LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>,
	'id' | 'children'
> & {
	isLabel?: boolean
	text?: ReactNode
	icon?: IconName
	isRequired?: boolean
	tooltip?: string
}

export const Input: FunctionComponent<InputProps> = ({
	id,
	name,
	type,
	autoComplete,
	value,
	onChange,
	disabled,
	readOnly,
	placeholder,
	required,
	iconDecoration,
	label,
	reactHookForm,
	explanation,
}) => {
	return (
		<div className={clsx(styles.wrapper, iconDecoration && styles.is_iconDecoration)}>
			<InputLabel {...label} isRequired={required} tooltip={explanation}>
				<span className={styles.inputWrapper}>
					<input
						id={id}
						className={styles.input}
						name={name}
						type={type}
						autoComplete={autoComplete}
						readOnly={readOnly}
						placeholder={placeholder}
						value={value}
						onChange={onChange}
						disabled={disabled}
						required={required}
						{...reactHookForm}
					/>
					{iconDecoration && (
						<span className={styles.inputIcon}>
							<Icon name={iconDecoration} />
						</span>
					)}
				</span>
			</InputLabel>
		</div>
	)
}

const InputLabel: FunctionComponent<LabelProps> = ({
	isLabel,
	children,
	isRequired,
	text,
	icon,
	tooltip,
}) => {
	if (isLabel) {
		return (
			<label className={styles.label}>
				{text && (
					<span className={styles.labelContent}>
						{icon && (
							<span className={styles.labelIcon}>
								<Icon name={icon} />
							</span>
						)}
						<span className={styles.labelName}>
							{text}
							{isRequired && '*'}
						</span>
						{tooltip && <Tooltip text={tooltip} />}
					</span>
				)}
				{children}
			</label>
		)
	}

	return <>{children}</>
}
